/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Accessor, Setter, Signal } from "solid-js";
import { createUniqueId, untrack } from "solid-js";
import type { SetStoreFunction, Store } from "solid-js/store";
import { reconcile } from "solid-js/store";
import { isDev, isServer } from "solid-js/web";

/**
 * 持久化存储工具，用于将 Solid.js 的状态（Signal 或 Store）持久化到存储介质中
 * 修复了@solid-primitives/storage 包的 bug，解决了 SSR 模式下页面状态初始化后不更新的问题
 *
 * @file Persisted.ts
 * @author jaxson
 * @example
 *   // 使用示例：
 *   const [store, setStore, init] = makePersisted(
 *     createStore({ count: 0 }),
 *     {
 *       storage: localStorage,
 *       name: "my-store",
 *     },
 *   );
 *
 * @github https://github.com/solidjs-community/solid-primitives/tree/main/packages/storage#readme
 * @created 2025-04-10
 * @modified 2025-04-10
 */

import type { PersistenceOptions } from "@solid-primitives/storage";

type SignalInput = Signal<any> | [Store<any>, SetStoreFunction<any>];

type SignalType<S extends SignalInput> =
  S extends Signal<infer T>
    ? T
    : S extends [Store<infer T>, SetStoreFunction<infer T>]
      ? T
      : never;

type PersistedState<S extends SignalInput> =
  S extends Signal<infer T>
    ? [get: Accessor<T>, set: Setter<T>, init: Promise<string> | string | null]
    : S extends [Store<infer T>, SetStoreFunction<infer T>]
      ? [
          get: Store<T>,
          set: SetStoreFunction<T>,
          init: Promise<string> | string | null,
        ]
      : never;

type PersistenceSyncData = {
  key: string;
  newValue: string | null | undefined;
  timeStamp: number;
  url?: string;
};
/**
 * Persists a signal, store or similar API
 *
 * ```ts
 * const [getter, setter] = makePersisted(createSignal("data"), options);
 * const options = {
 *   storage: cookieStorage,  // can be any synchronous or asynchronous storage
 *   storageOptions: { ... }, // for storages with options, otherwise not needed
 *   name: "solid-data",      // optional
 *   serialize: (value: string) => value, // optional
 *   deserialize: (data: string) => data, // optional
 * };
 * ```
 *
 * Can be used with `createSignal` or `createStore`. The initial value from the
 * storage will overwrite the initial value of the signal or store unless
 * overwritten. Overwriting a signal with `null` or `undefined` will remove the
 * item from the storage.
 *
 * @param {Signal<T> | [get: Store<T>, set: SetStoreFunction<T>]} signal - The
 *   signal or store to be persisted.
 * @param {PersistenceOptions<T, O>} options - The options for persistence.
 * @returns {PersistedState<T>} - The persisted signal or store.
 */
export function makePersisted<S extends SignalInput>(
  signal: S,
  options?: PersistenceOptions<SignalType<S>, undefined>,
): PersistedState<S>;
export function makePersisted<
  S extends SignalInput,
  O extends Record<string, any>,
>(signal: S, options: PersistenceOptions<SignalType<S>, O>): PersistedState<S>;
export function makePersisted<
  S extends SignalInput,
  O extends Record<string, any> | undefined,
  T = SignalType<S>,
>(
  signal: S,
  options: PersistenceOptions<T, O> = {} as PersistenceOptions<T, O>,
): PersistedState<S> {
  const storage =
    options.storage || (globalThis.localStorage as Storage | undefined);
  const name = options.name || `storage-${createUniqueId()}`;
  if (!storage) {
    return [signal[0], signal[1], null] as PersistedState<S>;
  }
  const storageOptions = (options as unknown as { storageOptions: O })
    .storageOptions;
  const serialize: (data: T) => string =
    options.serialize || JSON.stringify.bind(JSON);
  const deserialize: (data: string) => T =
    options.deserialize || JSON.parse.bind(JSON);
  const init = storage.getItem(name, storageOptions);
  const set =
    typeof signal[0] === "function"
      ? (data: string) => {
          try {
            const value = deserialize(data);
            (signal[1] as any)(() => value);
          } catch (e) {
            if (isDev) console.warn(e);
          }
        }
      : (data: string) => {
          try {
            const value = deserialize(data);
            (signal[1] as any)(reconcile(value));
          } catch (e) {
            if (isDev) console.warn(e);
          }
        };
  let unchanged = true;

  if (init instanceof Promise) {
    init.then((data) => unchanged && data && set(data));
  } else if (init) {
    queueMicrotask(() => {
      set(init);
    });
  }

  if (typeof options.sync?.[0] === "function") {
    const get: () => T =
      typeof signal[0] === "function"
        ? (signal[0] as () => T)
        : () => signal[0] as T;
    options.sync[0]((data: PersistenceSyncData) => {
      if (
        data.key !== name ||
        (!isServer &&
          (data.url || globalThis.location.href) !==
            globalThis.location.href) ||
        data.newValue === serialize(untrack(get))
      ) {
        return;
      }
      set(data.newValue as string);
    });
  }

  return [
    signal[0],
    typeof signal[0] === "function"
      ? (value?: T | ((prev: T) => T)) => {
          const output = (signal[1] as Setter<T>)(value as any);
          const serialized: string | null | undefined =
            value != null ? serialize(output) : (value as null | undefined);
          options.sync?.[1](name, serialized);
          if (serialized != null)
            storage.setItem(name, serialized, storageOptions);
          else storage.removeItem(name, storageOptions);
          unchanged = false;
          return output;
        }
      : (...args: any[]) => {
          (signal[1] as any)(...args);
          const value = serialize(untrack(() => signal[0]));
          options.sync?.[1](name, value);
          storage.setItem(name, value, storageOptions);
          unchanged = false;
        },
    init,
  ] as PersistedState<S>;
}
